import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import type { ApiResponse } from '@/types/api'
import {
  ChevronDown,
  ChevronRight,
  Database,
  Loader2,
  RefreshCw,
  ArrowUp,
  ArrowDown,
  Save,
  Check,
  AlertTriangle,
} from 'lucide-react'

// ============================================================
// Field Stats Types & Components (기존)
// ============================================================

interface TableFieldStats {
  tableName: string
  fieldName: string
  counts: Record<string, number>
  total: number
}

interface StatField {
  table: string
  field: string
  label: string
}

const STAT_FIELDS: { group: string; fields: StatField[] }[] = [
  {
    group: 'quiz',
    fields: [
      { table: 'quiz', field: 'difficulty_level', label: 'Difficulty Level' },
      { table: 'quiz', field: 'aiq', label: 'AIQ' },
      { table: 'quiz', field: 'adult', label: 'Adult' },
    ],
  },
]

function formatValue(field: string, value: string): string {
  if (field === 'aiq' || field === 'adult') {
    return value === 'true' ? 'Yes' : 'No'
  }
  return value
}

function useFieldStats(table: string, field: string) {
  return useQuery<TableFieldStats>({
    queryKey: ['data-stats', table, field],
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<TableFieldStats>>(
        '/admin/data-stats',
        { params: { table, field } }
      )
      if (res.data.success && res.data.data) {
        return res.data.data
      }
      throw new Error('데이터 조회 실패')
    },
    enabled: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

function StatsCard({ statField }: { statField: StatField }) {
  const [expanded, setExpanded] = useState(false)
  const { data, isFetching, error, refetch } = useFieldStats(statField.table, statField.field)

  const handleClick = () => {
    if (data) {
      setExpanded(!expanded)
      return
    }
    refetch()
    setExpanded(true)
  }

  const sortedEntries = data
    ? Object.entries(data.counts).sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
    : []

  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
      <button
        onClick={handleClick}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          {isFetching ? (
            <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
          ) : expanded ? (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400" />
          )}
          <div>
            <span className="font-medium text-slate-900">{statField.label}</span>
            <span className="ml-2 text-xs text-slate-400 font-mono">
              {statField.table}.{statField.field}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <span className="text-sm text-slate-500 font-medium tabular-nums">
              Total: {data.total.toLocaleString()}
            </span>
          )}
          {data && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); refetch() }}
              className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
              title="재조회"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      </button>

      {expanded && data && (
        <div className="border-t border-slate-100 px-5 py-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-100">
                <th className="pb-2 font-medium">값</th>
                <th className="pb-2 font-medium text-right">레코드 수</th>
                <th className="pb-2 font-medium text-right w-32">비율</th>
              </tr>
            </thead>
            <tbody>
              {sortedEntries.map(([value, count]) => {
                const pct = data.total > 0 ? (count / data.total) * 100 : 0
                return (
                  <tr key={value} className="border-b border-slate-50 last:border-0">
                    <td className="py-2 font-mono text-slate-800">
                      {formatValue(statField.field, value)}
                    </td>
                    <td className="py-2 text-right tabular-nums text-slate-700">
                      {count.toLocaleString()}
                    </td>
                    <td className="py-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-20 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-amber-500 rounded-full"
                            style={{ width: `${Math.min(pct, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-500 tabular-nums w-12 text-right">
                          {pct.toFixed(1)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {error && !expanded && (
        <div className="px-5 py-3 border-t border-slate-100">
          <p className="text-sm text-red-500">{error instanceof Error ? error.message : '오류 발생'}</p>
        </div>
      )}
    </div>
  )
}

function FieldStatsTab() {
  return (
    <div className="space-y-6">
      {STAT_FIELDS.map((group) => (
        <div key={group.group}>
          <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <span className="px-2 py-0.5 bg-slate-100 rounded text-sm font-mono">
              {group.group}
            </span>
            테이블
          </h2>
          <div className="space-y-2">
            {group.fields.map((f) => (
              <StatsCard key={`${f.table}.${f.field}`} statField={f} />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ============================================================
// Course Order Types & Components (신규)
// ============================================================

interface CourseOrderItem {
  courseId: number
  courseTitle: string
  sequenceOrderInTarget: number
  overallSequenceOrder: number | null
  belongsToCount: number
}

interface CurriculumCourseOrderResponse {
  curriculumId: number
  curriculumTitle: string
  courses: CourseOrderItem[]
}

interface UpdateCourseOrderRequest {
  curriculumId: number
  courseOrders: { courseId: number; sequenceOrder: number }[]
}

// Local state per curriculum: edited course list
type DirtyState = Record<number, CourseOrderItem[]>

function CurriculumCard({
  curriculum,
  dirtyState,
  onReorder,
  onSave,
  isSaving,
  saveSuccess,
}: {
  curriculum: CurriculumCourseOrderResponse
  dirtyState: CourseOrderItem[] | undefined
  onReorder: (curriculumId: number, courses: CourseOrderItem[]) => void
  onSave: (curriculumId: number) => void
  isSaving: boolean
  saveSuccess: number | null
}) {
  const courses = dirtyState ?? curriculum.courses
  const isDirty = !!dirtyState

  const moveUp = (index: number) => {
    if (index === 0) return
    const next = [...courses]
    ;[next[index - 1], next[index]] = [next[index], next[index - 1]]
    onReorder(curriculum.curriculumId, next)
  }

  const moveDown = (index: number) => {
    if (index === courses.length - 1) return
    const next = [...courses]
    ;[next[index], next[index + 1]] = [next[index + 1], next[index]]
    onReorder(curriculum.curriculumId, next)
  }

  const justSaved = saveSuccess === curriculum.curriculumId

  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900">{curriculum.curriculumTitle}</h3>
          <span className="text-xs text-slate-400 font-mono">ID: {curriculum.curriculumId}</span>
        </div>
        <div className="flex items-center gap-2">
          {justSaved && (
            <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
              <Check className="w-3.5 h-3.5" /> 저장 완료
            </span>
          )}
          <button
            onClick={() => onSave(curriculum.curriculumId)}
            disabled={!isDirty || isSaving}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              isDirty
                ? 'bg-amber-500 text-white hover:bg-amber-600'
                : 'bg-slate-100 text-slate-400 cursor-not-allowed'
            }`}
          >
            {isSaving ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Save className="w-3.5 h-3.5" />
            )}
            저장
          </button>
        </div>
      </div>

      <div className="divide-y divide-slate-50">
        {courses.map((course, index) => (
          <div
            key={course.courseId}
            className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors"
          >
            <span className="w-8 text-center text-sm font-mono text-slate-400 font-semibold">
              {index + 1}
            </span>
            <div className="flex-1 min-w-0">
              <span className="text-sm text-slate-900 font-medium truncate block">
                {course.courseTitle}
              </span>
              <div className="flex items-center gap-3 mt-0.5">
                <span className="text-xs text-slate-400 font-mono">ID: {course.courseId}</span>
                <span className="text-xs text-slate-400">
                  seq_in_target: {course.sequenceOrderInTarget}
                </span>
                {course.overallSequenceOrder != null && (
                  <span className="text-xs text-slate-400">
                    overall: {course.overallSequenceOrder}
                  </span>
                )}
                {course.belongsToCount >= 2 && (
                  <span className="inline-flex items-center gap-0.5 text-xs text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded font-medium">
                    <AlertTriangle className="w-3 h-3" />
                    {course.belongsToCount}개 커리큘럼
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => moveUp(index)}
                disabled={index === 0}
                className={`p-1.5 rounded transition-colors ${
                  index === 0
                    ? 'text-slate-200 cursor-not-allowed'
                    : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100'
                }`}
                title="위로 이동"
              >
                <ArrowUp className="w-4 h-4" />
              </button>
              <button
                onClick={() => moveDown(index)}
                disabled={index === courses.length - 1}
                className={`p-1.5 rounded transition-colors ${
                  index === courses.length - 1
                    ? 'text-slate-200 cursor-not-allowed'
                    : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100'
                }`}
                title="아래로 이동"
              >
                <ArrowDown className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function CourseOrderTab() {
  const queryClient = useQueryClient()
  const [dirtyState, setDirtyState] = useState<DirtyState>({})
  const [saveSuccess, setSaveSuccess] = useState<number | null>(null)

  const { data, isLoading, error, refetch } = useQuery<CurriculumCourseOrderResponse[]>({
    queryKey: ['course-orders'],
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<CurriculumCourseOrderResponse[]>>(
        '/admin/data-stats/course-orders'
      )
      if (res.data.success && res.data.data) {
        return res.data.data
      }
      throw new Error('코스 순서 조회 실패')
    },
  })

  const mutation = useMutation<CurriculumCourseOrderResponse, Error, UpdateCourseOrderRequest>({
    mutationFn: async (req) => {
      const res = await apiClient.put<ApiResponse<CurriculumCourseOrderResponse>>(
        '/admin/data-stats/course-orders',
        req
      )
      if (res.data.success && res.data.data) {
        return res.data.data
      }
      throw new Error('코스 순서 저장 실패')
    },
    onSuccess: (updated) => {
      // Remove from dirty state
      setDirtyState((prev) => {
        const next = { ...prev }
        delete next[updated.curriculumId]
        return next
      })
      // Update cache
      queryClient.setQueryData<CurriculumCourseOrderResponse[]>(['course-orders'], (old) =>
        old?.map((c) => (c.curriculumId === updated.curriculumId ? updated : c))
      )
      // Show success badge
      setSaveSuccess(updated.curriculumId)
      setTimeout(() => setSaveSuccess(null), 2000)
    },
  })

  const handleReorder = useCallback((curriculumId: number, courses: CourseOrderItem[]) => {
    setDirtyState((prev) => ({ ...prev, [curriculumId]: courses }))
  }, [])

  const handleSave = useCallback(
    (curriculumId: number) => {
      const courses = dirtyState[curriculumId]
      if (!courses) return
      mutation.mutate({
        curriculumId,
        courseOrders: courses.map((c, i) => ({
          courseId: c.courseId,
          sequenceOrder: i + 1,
        })),
      })
    },
    [dirtyState, mutation]
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
        <span className="ml-2 text-slate-500">커리큘럼 데이터 조회 중...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-red-500">{error instanceof Error ? error.message : '오류 발생'}</p>
        <button
          onClick={() => refetch()}
          className="mt-3 text-sm text-amber-600 hover:underline"
        >
          다시 시도
        </button>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-16 text-slate-500">
        2개 이상의 커리큘럼에 속한 공유 코스가 없습니다.
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          2개 이상의 커리큘럼에 속한 공유 코스를 포함한 커리큘럼 {data.length}개
        </p>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" /> 새로고침
        </button>
      </div>

      {mutation.isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
          {mutation.error instanceof Error ? mutation.error.message : '저장 중 오류가 발생했습니다.'}
        </div>
      )}

      {data.map((curriculum) => (
        <CurriculumCard
          key={curriculum.curriculumId}
          curriculum={curriculum}
          dirtyState={dirtyState[curriculum.curriculumId]}
          onReorder={handleReorder}
          onSave={handleSave}
          isSaving={mutation.isPending && mutation.variables?.curriculumId === curriculum.curriculumId}
          saveSuccess={saveSuccess}
        />
      ))}
    </div>
  )
}

// ============================================================
// Main Page
// ============================================================

type TabId = 'field-stats' | 'course-order'

const TABS: { id: TabId; label: string }[] = [
  { id: 'field-stats', label: '필드 통계' },
  { id: 'course-order', label: '코스 순서 관리' },
]

export function DataStatsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('field-stats')

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
          <Database className="w-7 h-7 text-amber-500" />
          DATA 확인
        </h1>
        <p className="mt-1 text-slate-500">
          데이터베이스 테이블의 필드별 레코드 수를 확인하거나 커리큘럼별 코스 순서를 관리합니다.
        </p>
      </div>

      <div className="border-b border-slate-200">
        <nav className="flex gap-0 -mb-px">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-amber-500 text-amber-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'field-stats' && <FieldStatsTab />}
      {activeTab === 'course-order' && <CourseOrderTab />}
    </div>
  )
}
